import { EAIClient } from "../../services/EAIClient";
import { Utils } from "../utils";
import CONFIG from "Config";

export interface AuthResult {
  idToken: string;
  accessToken: string;
  expiresIn?: number;
  tokenType?: string;
  email?: string;
}

export interface AuthError {
  error: string;
  errorDescription?: string;
  description?: string;
}

export abstract class BaseAuth {
  protected EAIClient: EAIClient;

  constructor() {
    this.EAIClient = new EAIClient();
  }

  // Métodos que podem ser sobrescritos pelas classes filhas
  login(username: string, password: string, onError: (err: AuthError) => void): void {
    throw new Error('login method must be implemented by subclass');
  }

  loginRedirect(): void {
    throw new Error('loginRedirect method must be implemented by subclass');
  }

  authorizeSSOConnection(connection: string): void {
    throw new Error('authorizeSSOConnection method must be implemented by subclass');
  }

  resetPassword(email: string, onError: (err: AuthError) => void, onSuccess: (resp: any) => void): void {
    throw new Error('resetPassword method must be implemented by subclass');
  }

  handleAuthentication(): void {
    throw new Error('handleAuthentication method must be implemented by subclass');
  }

  handleLogout(callback: () => void): void {
    throw new Error('handleLogout method must be implemented by subclass');
  }

  postLoginRedirect() {
    let redirect = localStorage.getItem("post_login_redirect");
    redirect = redirect === "/callback" || redirect === "/login" ? "/" : redirect;
    location.replace(redirect || Utils.getLinkBase());
    localStorage.removeItem("post_login_redirect");
  }

  handleTokenRenew = async () => {
    const resp = await this.EAIClient.renewTokens();
    Utils.setStorageValues(resp.data.data);
  };

  getAuthMethod = async (path: string) => {
    const resp = await this.EAIClient.getAuthMethod(path);
    return resp.data.data;
  };

  getUniversityUrl = async (email: string) => {
    const resp = await this.EAIClient.getUniversityUrl(email);
    return resp.data.data;
  };

  isAuthenticated = () => {
    // Keys to check in localstorage.
    const localStorageValidation = {
      current_user: "json",
      expires_at: "number",
      is_superadmin: "boolean",
      // school_list is a comma separated list, just check that it isn't null
      school_list: "",
      chosen_school_info: "json",
    };

    let areValuesGood = true;

    Object.keys(localStorageValidation).forEach(key => {
      const value = localStorage.getItem(key);
      if(value === null){
        areValuesGood = false;
      } else {
        switch(localStorageValidation[key]) {
          case "json":
            try{
              JSON.parse(value);
            } catch(e){
              console.error(`Bad json value in localstorage key: ${key}`, { value });
              areValuesGood = false;
            }
            break;
          case "boolean":
            if(!["true", "false"].includes(value.toLowerCase())){
              console.error(`Bad boolean value in localstorage for key ${key}`, { value });
              areValuesGood = false;
            }
            break;
          case "number":
            if(isNaN(parseInt(value))){
              console.error(`Bad number value in localstorage for key ${key}`, { value });
              areValuesGood = false;
            }
            break;
          default:
            break;
        }
      }
    });

    if(!areValuesGood){
      // If a value in localStorage is bad clear them and
      //  have the user log in again to set them
      Utils.clearLocalstorage();
      return false;
    }

    // Otherwise, check the expiry listed in localstorage
    // The EAIClient will still log them out if their cookies have expired
    const expiresAtVal = localStorage.getItem("expires_at");
    if (!expiresAtVal) {
      return false;
    }
    const expiresAt = parseInt(expiresAtVal);

    return expiresAt ? new Date(expiresAt * 1000) > new Date() : false;
  };

  protected handleAuthSuccess = (authResult: AuthResult) => {
    const selectedSchoolId = localStorage.getItem("chosen_school");
    // If chosen_school is set, pass it along with tokens to allow api to do login events
    // If not set or if not in user's school list, api will use the first school in
    //   the user's list (which we will then default to as well)
    this.EAIClient.logIn(authResult.idToken, authResult.accessToken, selectedSchoolId, authResult.email, window.location.hostname).then((resp) => {
      Utils.setStorageValues(resp.data.data, this.postLoginRedirect);
    }).catch((err) => {
      const data = err.response.data;
      if (data.result.error === "UserNotActive") {
        localStorage.setItem("authentication_error", "You are not currently configured in Enflux. Please ask your Enflux administrator to add you to Enflux");
      }

      return location.replace(CONFIG.paths.base + "login");
    });
  };

  protected handleAuthError = (err: AuthError) => {
    let errorMessage = "Authentication Result Invalid";
    if (err && (err.errorDescription || err.description)) {
      errorMessage = err.errorDescription || err.description || errorMessage;
    }
    localStorage.setItem("authentication_error", errorMessage);
    return location.replace(CONFIG.paths.base + "login");
  };
} 